import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Trophy } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import apiClient from '@/services/api';
import type { SalesSummaryReport } from '@/types/api';
import { KPICard } from '@/components/reports/KPICard';
import { ReportFrame, SimpleTable } from '@/components/reports/ReportFrame';
import type { ReportViewProps } from '@/components/reports/reportTypes';
import { buildGenericCsv, downloadCsv, printGenericReport, type ReportSection } from '@/utils/reportExport';

/** Server Ranking: leaderboard of servers by sales, with rank, avg check and share. */
export default function ServerRankingReport({ filters }: ReportViewProps) {
  const { selectedRestaurantId, startDate, endDate, formatCurrency, selectedRestaurant } = filters;

  const { data, isLoading, isError, refetch } = useQuery<SalesSummaryReport>({
    queryKey: ['sales-summary', selectedRestaurantId, startDate, endDate],
    queryFn: () => apiClient.getSalesSummary(selectedRestaurantId!, startDate, endDate),
    enabled: !!selectedRestaurantId,
  });

  const ranked = [...(data?.byServer ?? [])].sort((a, b) => Number(b.total) - Number(a.total));
  const totalSales = Number(data?.totals?.total ?? 0);
  const top = ranked[0];
  const share = (total: number) => (totalSales > 0 ? `${((total / totalSales) * 100).toFixed(1)}%` : '—');
  const check = (total: number, orders: number) => (orders > 0 ? total / orders : 0);
  const chart = ranked.slice(0, 15).map((s) => ({ name: s.serverName, total: Number(s.total) }));
  const meta = () => ({ restaurantName: selectedRestaurant?.name ?? '', startDate, endDate, formatCurrency });

  const sections = (): ReportSection[] => [
    {
      heading: 'Server Ranking',
      headers: ['Rank', 'Server', 'Orders', 'Avg Check', 'Share', 'Total'],
      rows: ranked.map((s, i) => [i + 1, s.serverName, s.orders, formatCurrency(check(Number(s.total), s.orders)), share(Number(s.total)), formatCurrency(s.total)]),
    },
  ];
  const kpis = (): [string, string][] => [
    ['Top Server', top?.serverName ?? '—'],
    ['Top Sales', formatCurrency(top?.total ?? 0)],
    ['Servers', ranked.length.toLocaleString()],
  ];

  return (
    <ReportFrame
      title="Server Ranking"
      description="Leaderboard of servers by sales, with rank, average check and share of total."
      hasRestaurant={!!selectedRestaurantId}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      isEmpty={ranked.length === 0}
      exportDisabled={!data}
      onExportCsv={() => data && downloadCsv(`server-ranking-${format(new Date(), 'yyyy-MM-dd')}.csv`, buildGenericCsv('Server Ranking', meta(), kpis(), sections()))}
      onExportPdf={() => data && printGenericReport('Server Ranking Report', meta(), kpis(), sections())}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard label="Top Server" value={top?.serverName ?? '—'} icon={Trophy} palette="green" />
        <KPICard label="Top Sales" value={formatCurrency(top?.total ?? 0)} icon={Trophy} palette="blue" />
        <KPICard label="Servers" value={ranked.length.toLocaleString()} icon={Trophy} palette="purple" />
      </div>
      <div className="card"><div className="card-body">
        <h3 className="text-lg font-semibold mb-4">Top 15 by Sales</h3>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={chart} layout="vertical" margin={{ left: 40 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis type="category" dataKey="name" width={120} />
            <Tooltip />
            <Bar dataKey="total" name="Sales" fill="#00C49F" />
          </BarChart>
        </ResponsiveContainer>
      </div></div>
      <div className="card"><div className="card-body">
        <h3 className="text-lg font-semibold mb-4">Ranking</h3>
        <SimpleTable headers={['#', 'Server', 'Orders', 'Avg Check', 'Share', 'Total']}
          rows={ranked.map((s, i) => [i + 1, s.serverName, s.orders, formatCurrency(check(Number(s.total), s.orders)), share(Number(s.total)), formatCurrency(s.total)])} />
      </div></div>
    </ReportFrame>
  );
}
